import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { BarChart3, PieChart as PieChartIcon, Users, Building2, Layers, Activity, Filter, X, RefreshCw } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
];

const EmployeeAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    role_distribution: [],
    department_distribution: [],
    level_distribution: [],
    status_distribution: [],
    gender_distribution: [],
    disability_distribution: [],
    employee_type_distribution: [],
    total_employees: 0
  });
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYears, setSelectedYears] = useState([]);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchAvailableYears();
    fetchData();
  }, []);

  const fetchAvailableYears = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/analytics/available-years`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableYears(response.data);
    } catch (error) {
      console.error('Failed to fetch years:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (selectedYears.length > 0) {
        params.append('years', selectedYears.join(','));
      }
      if (selectedMonths.length > 0) {
        params.append('months', selectedMonths.join(','));
      }
      
      const url = `${API}/analytics/employee-distribution${params.toString() ? '?' + params.toString() : ''}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load analytics data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleYearToggle = (year) => {
    setSelectedYears(prev => 
      prev.includes(year) 
        ? prev.filter(y => y !== year)
        : [...prev, year]
    );
  };

  const handleMonthToggle = (month) => {
    setSelectedMonths(prev =>
      prev.includes(month)
        ? prev.filter(m => m !== month)
        : [...prev, month]
    );
  };

  const clearFilters = () => {
    setSelectedYears([]);
    setSelectedMonths([]);
  };

  const applyFilters = () => {
    fetchData();
    setShowFilters(false);
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 shadow-lg rounded-lg border">
          <p className="font-medium">{payload[0].name}</p>
          <p className="text-gray-600">Count: <span className="font-semibold">{payload[0].value}</span></p>
        </div>
      );
    }
    return null;
  };

  const RADIAN = Math.PI / 180;
  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, name, value, percent }) => {
    if (percent === 0) return null;
    // For very dominant slices (>95%), place label inside the donut center
    if (percent > 0.95) {
      return (
        <text x={cx} y={cy} fill="#374151" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={600}>
          {name}: {value}
        </text>
      );
    }
    const radius = outerRadius + 25;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text x={x} y={y} fill="#374151" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={11}>
        {name}: {value}
      </text>
    );
  };

  const renderPieChart = (chartData, title, icon, testId) => {
    // Filter out zero-value entries from chart rendering
    const activeData = chartData.filter(item => item.value > 0);
    const total = chartData.reduce((sum, item) => sum + item.value, 0);
    return (
      <Card className="h-full" data-testid={testId}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            {icon}
            {title}
          </CardTitle>
          <CardDescription>
            {chartData.length > 0 
              ? `${total} total`
              : 'No data available'
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {activeData.length > 0 ? (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={activeData}
                    cx="50%"
                    cy="50%"
                    innerRadius={45}
                    outerRadius={75}
                    paddingAngle={activeData.length > 1 ? 2 : 0}
                    dataKey="value"
                    label={renderCustomLabel}
                    labelLine={false}
                  >
                    {activeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const pct = total > 0 ? ((payload[0].value / total) * 100).toFixed(1) : 0;
                      return (
                        <div className="bg-white p-3 shadow-lg rounded-lg border">
                          <p className="font-medium">{payload[0].name}</p>
                          <p className="text-gray-600">Count: <span className="font-semibold">{payload[0].value}</span></p>
                          <p className="text-gray-600">Percentage: <span className="font-semibold">{pct}%</span></p>
                        </div>
                      );
                    }
                    return null;
                  }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-400">
              No data to display
            </div>
          )}
          {chartData.length > 0 && (
            <div className="mt-4 space-y-1.5">
              {chartData.map((item, index) => {
                const pct = total > 0 ? ((item.value / total) * 100).toFixed(1) : 0;
                return (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                      <span className="text-gray-700">{item.name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-semibold text-gray-900">{item.value}</span>
                      <span className="text-gray-500 w-14 text-right">({pct}%)</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderBarChart = (chartData, title, icon) => (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          {icon}
          {title}
        </CardTitle>
        <CardDescription>
          {chartData.length > 0 
            ? `${chartData.length} categories`
            : 'No data available'
          }
        </CardDescription>
      </CardHeader>
      <CardContent>
        {chartData.length > 0 ? (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis 
                  dataKey="name" 
                  type="category" 
                  width={140}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-64 flex items-center justify-center text-gray-400">
            No data to display
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Header with Filters */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <BarChart3 className="h-8 w-8 text-blue-600" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Employee Analytics</h2>
            <p className="text-sm text-gray-500">
              {data.total_employees} employees
              {(selectedYears.length > 0 || selectedMonths.length > 0) && ' (filtered)'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2"
            data-testid="toggle-filters-btn"
          >
            <Filter className="h-4 w-4" />
            Filters
            {(selectedYears.length > 0 || selectedMonths.length > 0) && (
              <Badge variant="secondary" className="ml-1">
                {selectedYears.length + selectedMonths.length}
              </Badge>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={fetchData}
            disabled={loading}
            data-testid="refresh-analytics-btn"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="pt-4">
            <div className="space-y-4">
              {/* Year Filters */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Filter by Year (based on Date of Joining)
                </label>
                <div className="flex flex-wrap gap-2">
                  {availableYears.map(year => (
                    <Button
                      key={year}
                      variant={selectedYears.includes(year) ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleYearToggle(year)}
                      data-testid={`year-filter-${year}`}
                    >
                      {year}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Month Filters */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Filter by Month
                </label>
                <div className="flex flex-wrap gap-2">
                  {MONTHS.map(month => (
                    <Button
                      key={month.value}
                      variant={selectedMonths.includes(month.value) ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleMonthToggle(month.value)}
                      data-testid={`month-filter-${month.value}`}
                    >
                      {month.label.substring(0, 3)}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Filter Actions */}
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="ghost" onClick={clearFilters} size="sm">
                  <X className="h-4 w-4 mr-1" />
                  Clear All
                </Button>
                <Button onClick={applyFilters} size="sm" data-testid="apply-filters-btn">
                  Apply Filters
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Filters Display */}
      {(selectedYears.length > 0 || selectedMonths.length > 0) && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-gray-500">Active filters:</span>
          {selectedYears.map(year => (
            <Badge key={`year-${year}`} variant="secondary" className="flex items-center gap-1">
              Year: {year}
              <X 
                className="h-3 w-3 cursor-pointer" 
                onClick={() => {
                  handleYearToggle(year);
                  setTimeout(fetchData, 100);
                }}
              />
            </Badge>
          ))}
          {selectedMonths.map(month => (
            <Badge key={`month-${month}`} variant="secondary" className="flex items-center gap-1">
              {MONTHS.find(m => m.value === month)?.label}
              <X 
                className="h-3 w-3 cursor-pointer" 
                onClick={() => {
                  handleMonthToggle(month);
                  setTimeout(fetchData, 100);
                }}
              />
            </Badge>
          ))}
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className="h-96 animate-pulse">
              <CardHeader>
                <div className="h-6 bg-gray-200 rounded w-1/3"></div>
              </CardHeader>
              <CardContent>
                <div className="h-64 bg-gray-100 rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Gender Distribution - Pie Chart */}
          {renderPieChart(
            data.gender_distribution,
            'Gender Wise Distribution',
            <Users className="h-5 w-5 text-pink-500" />,
            'chart-gender'
          )}

          {/* Physically Disabled Distribution - Pie Chart */}
          {renderPieChart(
            data.disability_distribution,
            'Physically Disabled Distribution',
            <Activity className="h-5 w-5 text-red-500" />,
            'chart-disability'
          )}

          {/* Employee Type Distribution - Pie Chart */}
          {renderPieChart(
            data.employee_type_distribution,
            'Employee Type Distribution',
            <Layers className="h-5 w-5 text-violet-500" />,
            'chart-employee-type'
          )}

          {/* Role Distribution - Pie Chart */}
          {renderPieChart(
            data.role_distribution,
            'Role Distribution',
            <Users className="h-5 w-5 text-blue-500" />,
            'chart-role'
          )}

          {/* Status Distribution - Pie Chart */}
          {renderPieChart(
            data.status_distribution,
            'Status Distribution',
            <Activity className="h-5 w-5 text-green-500" />,
            'chart-status'
          )}

          {/* Department Distribution - Bar Chart */}
          {renderBarChart(
            data.department_distribution,
            'Department Distribution',
            <Building2 className="h-5 w-5 text-purple-500" />
          )}

          {/* Level Distribution - Bar Chart */}
          {renderBarChart(
            data.level_distribution,
            'Level Distribution',
            <Layers className="h-5 w-5 text-orange-500" />
          )}
        </div>
      )}
    </div>
  );
};

export default EmployeeAnalytics;
